<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import MIcon from '@/components/MIcon.vue'

interface Props {
  toastType?: 'error' | 'warning' | 'success'
  message: string
  icon?: string // Özel ikon ismi (opsiyonel)
  duration?: number
  onClose?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  toastType: 'success',
  duration: 3000,
})

const show = ref(false)
let closeTimeout: ReturnType<typeof setTimeout> | null = null

// Toast tipine göre renk ve ikon
const toastConfig = computed(() => {
  switch (props.toastType) {
    case 'error':
      return {
        bgColor: 'bg-red-50',
        iconColor: 'text-red-500',
        icon: 'error',
      }
    case 'warning':
      return {
        bgColor: 'bg-orange-50',
        iconColor: 'text-orange-500',
        icon: 'warning',
      }
    case 'success':
    default:
      return {
        bgColor: 'bg-green-50',
        iconColor: 'text-green-500',
        icon: 'check_circle',
      }
  }
})

const openToast = () => {
  show.value = true
  closeTimeout = setTimeout(() => {
    closeToast()
  }, props.duration)
}

const closeToast = () => {
  show.value = false
  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
  // onClose callback'i animasyon tamamlandıktan sonra çağrılacak (@after-leave event'inde)
}

// Animasyon tamamlandıktan sonra callback'i çağır
const handleAfterLeave = () => {
  if (props.onClose) {
    props.onClose()
  }
}

onMounted(() => {
  openToast()
})

onUnmounted(() => {
  if (closeTimeout) {
    clearTimeout(closeTimeout)
  }
})

defineExpose({
  openToast,
  closeToast,
})
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform -translate-y-full opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition duration-300 ease-in"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform -translate-y-full opacity-0"
    @after-leave="handleAfterLeave"
  >
    <div
      v-if="show"
      class="flex items-center gap-2 bg-white rounded-full pr-4 pl-2 py-2 max-w-[calc(100vw-32px)]"
      style="box-shadow: 0px 10px 16px 0px #00000029"
      role="alert"
    >
      <!-- İkon -->
      <span
        class="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
        :class="toastConfig.bgColor"
      >
        <MIcon :name="icon || toastConfig.icon" size="22px" :class="toastConfig.iconColor" />
      </span>

      <!-- Mesaj -->
      <p class="text-gray-800 font-body-md whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
        {{ message }}
      </p>
    </div>
  </Transition>
</template>
